/**
 * The API's ordering vocabulary for `GET /api/v1/actions` (feature 029, issue 109).
 *
 * `sort` is orthogonal to `view`: **`view` selects which actions, `sort` selects in what order.**
 *
 * Stated once, here, because a wire word spelled at each call site is the drift `core/recurrence/`
 * and `core/preferences/week-start.ts` already exist to prevent — and because the guard that asserts
 * the completed read does *not* carry it needs the same spelling the read uses. Two literals could
 * agree today and disagree after a rename.
 *
 * Note the deliberate collision with the list's `tag` FILTER parameter: `?tag=grocery` narrows a
 * list to one tag, `?sort=tag` arranges it by each action's primary tag. They are independent, and
 * no screen sends both — the tag view filters and never sorts, because ordering by primary tag would
 * scatter the very actions it was asked to show together.
 */
export const ACTION_SORTS = ['default', 'tag'] as const;

export type ActionSortWord = (typeof ACTION_SORTS)[number];

/**
 * The order every screen has had since 006 — container position, then due date (undated last), then
 * creation, then identity. Omitting `sort` means this, so no client needs to send it, and none does.
 */
export const ACTION_SORT_DEFAULT: ActionSortWord = 'default';

/**
 * Order by each action's PRIMARY tag — its alphabetically-first — as the second key, after container
 * position and before the due date. Requested by the container detail screens' open-actions read and
 * by nothing else.
 *
 * **It orders by one tag per action, not by every tag it carries.** An action tagged
 * `{errand, grocery}` sorts under `errand`, away from actions tagged `{grocery}`. That is the design
 * (spec FR-003), not a defect: the stronger "any shared tag" grouping needs one action to appear in a
 * list more than once, which breaks every count this application computes.
 */
export const ACTION_SORT_TAG: ActionSortWord = 'tag';
